/**
 * The scope screens' upload queue — `docs/activity.md` §11.4.
 *
 * Photos, videos, voice notes and notes made on a stop are written to
 * **IndexedDB first** (the blob, the records it goes to, its caption, the
 * activity it makes) and sent in order when the phone is online and the
 * server answers. Each one is retried with backoff — 5 s, doubling, never
 * more than 5 min — and kept until the server confirms it, so a photo taken
 * in a basement with no signal goes up in the truck, and a note written
 * then is still there if the app was closed in between.
 *
 * **Nothing goes twice.** A note carries a client id (`activities.client_id`,
 * §4.1): the same id again answers the row already made. A file keeps the
 * `Idempotency-Key` of a request whose answer never came back, so a retry is
 * replayed rather than repeated, and takes a new one only after the server
 * answered with a refusal (a replayed refusal would be a refusal for ever).
 * Attaching is `ON CONFLICT DO NOTHING` on the server either way.
 *
 * Only the scope screens use it; every other upload is as it always was.
 * The tiles of Board 15 B read it: a tick (up), a ring (uploading),
 * *waiting* (not yet).
 */
import { uuidv7 } from 'uuidv7'
import {
  ApiError, type ActivityInput, type FileType, type RecordRef,
} from '@rowted/shared'
import { currentCsrfToken, request } from '../../lib/api.js'
import { activityHeaders } from '../../lib/activity-device.js'

export type QueueState = 'waiting' | 'uploading' | 'done' | 'failed'

/** A photo, a video or a voice note's recording. */
export interface FileItem {
  clientId: string
  kind: 'file'
  /** One screen's worth (the photos of one visit to Board 15 B). */
  batch: string
  createdAt: number
  blob: Blob
  name: string
  contentType: string
  uploadKind: 'photo' | 'video' | 'audio'
  durationSeconds?: number
  orgUnitId?: string
  /** The records it is attached to: the job, then the property when *Also show* is on. */
  links: RecordRef[]
  /** The stop it was scoped on (§11.3). */
  source?: { kind: 'activity'; id: string }
  caption?: string
  /** A caption typed after it was attached, not yet on the server. */
  captionPending?: boolean
  type: FileType
  state: QueueState
  /** 0–1, while uploading. */
  progress: number
  fileId?: string
  /** A presigned upload under way: the pending row, and where the bytes go. */
  presigned?: { fileId: string; url: string; headers: Record<string, string>; put: boolean }
  /** The storage cannot sign: the bytes go through `POST /files`. */
  direct?: boolean
  /** How many of `links` are attached. */
  linked: number
  attempts: number
  nextAt: number
  /** The key of a request whose answer never came back. */
  key?: string
  error?: string
}

/** A note, a voice note or a call, made after the files it carries. */
export interface ActivityItem {
  clientId: string
  kind: 'activity'
  batch: string
  createdAt: number
  /** Posted to this record's activities; `payload.links` are the others. */
  record: RecordRef
  payload: Omit<ActivityInput, 'fileIds' | 'clientId' | 'visibility'> & { visibility?: ActivityInput['visibility'] }
  /** Files of this queue to attach once they are up. */
  fileClientIds: string[]
  state: QueueState
  progress: number
  activityId?: string
  attempts: number
  nextAt: number
  key?: string
  error?: string
}

export type QueueItem = FileItem | ActivityItem

// ---------------------------------------------------------------------------
// Where items are kept
// ---------------------------------------------------------------------------

/** Where the queue keeps what is not yet confirmed. IndexedDB on a phone; memory in a test. */
export interface QueueStore {
  all(): Promise<QueueItem[]>
  put(item: QueueItem): Promise<void>
  remove(clientId: string): Promise<void>
}

export function memoryStore(): QueueStore {
  const items = new Map<string, QueueItem>()
  return {
    all: async () => [...items.values()],
    put: async (item) => { items.set(item.clientId, item) },
    remove: async (clientId) => { items.delete(clientId) },
  }
}

const DB_NAME = 'rowted-scope'
const STORE = 'queue'

/** IndexedDB, opened once. A blob is stored as itself (structured clone). */
export function indexedDbStore(): QueueStore {
  let opened: Promise<IDBDatabase> | null = null
  const db = () => {
    opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const open = indexedDB.open(DB_NAME, 1)
      open.onupgradeneeded = () => {
        if (!open.result.objectStoreNames.contains(STORE)) open.result.createObjectStore(STORE, { keyPath: 'clientId' })
      }
      open.onsuccess = () => resolve(open.result)
      open.onerror = () => reject(open.error ?? new Error('IndexedDB would not open.'))
    })
    return opened
  }
  const run = <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
    db().then((handle) => new Promise<T>((resolve, reject) => {
      const tx = handle.transaction(STORE, mode)
      const req = work(tx.objectStore(STORE))
      tx.oncomplete = () => resolve(req.result)
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB refused the write.'))
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB refused the write.'))
    }))
  return {
    all: () => run('readonly', (store) => store.getAll() as IDBRequest<QueueItem[]>),
    put: (item) => run('readwrite', (store) => store.put(item)).then(() => undefined),
    remove: (clientId) => run('readwrite', (store) => store.delete(clientId)).then(() => undefined),
  }
}

// ---------------------------------------------------------------------------
// How items are sent
// ---------------------------------------------------------------------------

/** A request that never got an answer — the one kind of failure that keeps its key. */
export class NoAnswer extends Error {}

export interface Transport {
  /** `POST /files/uploads`: a presigned PUT, or `direct` where the storage cannot sign. */
  start(body: {
    name: string; contentType: string; byteSize: number; kind: string; orgUnitId?: string; durationSeconds?: number
  }, key: string): Promise<{ direct: true } | { direct?: false; fileId: string; url: string; headers: Record<string, string> }>
  put(url: string, headers: Record<string, string>, blob: Blob, onProgress: (fraction: number) => void): Promise<void>
  complete(fileId: string, key: string): Promise<void>
  /** `POST /files` with the bytes as the body. */
  direct(item: FileItem, key: string, onProgress: (fraction: number) => void): Promise<string>
  link(record: RecordRef, body: Record<string, unknown>, key: string): Promise<void>
  /** A caption on a file already attached. */
  caption(record: RecordRef, fileId: string, caption: string, key: string): Promise<void>
  activity(record: RecordRef, payload: Record<string, unknown>, key: string): Promise<string>
  online(): boolean
}

/** Anything over 20 MB, and every video, goes straight to the bucket (§11.3). */
export const PRESIGN_FROM_BYTES = 20 * 1024 * 1024

/** An XHR, for the progress ring; `NoAnswer` when the connection dropped. */
function send(method: string, url: string, headers: Record<string, string>, body: Blob | null,
  onProgress?: (fraction: number) => void): Promise<{ status: number; text: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)
    xhr.withCredentials = url.startsWith('/')
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value)
    if (onProgress) xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(event.loaded / event.total) }
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText })
    xhr.onerror = () => reject(new NoAnswer('No answer from the server.'))
    xhr.ontimeout = () => reject(new NoAnswer('No answer from the server.'))
    xhr.send(body)
  })
}

function refusal(status: number, text: string, fallback: string): ApiError {
  let body: { code?: string; message?: string } | null = null
  try { body = JSON.parse(text) as { code?: string; message?: string } } catch { /* not JSON */ }
  return new ApiError((body?.code ?? 'internal_error') as ApiError['code'], body?.message ?? fallback, { status })
}

/** Through `request`, with a dropped connection told apart from an answer. */
async function asked<T>(path: string, options: Parameters<typeof request>[1]): Promise<T> {
  try {
    return await request<T>(path, options)
  } catch (cause) {
    if (cause instanceof ApiError) throw cause
    throw new NoAnswer('No answer from the server.')
  }
}

export const httpTransport: Transport = {
  start: (body, key) => asked('/files/uploads', { method: 'POST', body, idempotencyKey: key }),
  async put(url, headers, blob, onProgress) {
    const answer = await send('PUT', url, headers, blob, onProgress)
    if (answer.status >= 300) throw new NoAnswer('The upload did not finish.')
  },
  complete: (fileId, key) =>
    asked<void>(`/files/uploads/${fileId}/complete`, { method: 'POST', body: {}, idempotencyKey: key }),
  async direct(item, key, onProgress) {
    const query = new URLSearchParams({ name: item.name, kind: item.uploadKind })
    if (item.orgUnitId) query.set('orgUnitId', item.orgUnitId)
    if (item.durationSeconds !== undefined) query.set('durationSeconds', String(item.durationSeconds))
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': item.contentType || 'application/octet-stream',
      'Idempotency-Key': key,
    }
    const csrf = currentCsrfToken()
    if (csrf) headers['x-rowted-csrf'] = csrf
    const answer = await send('POST', `/api/v1/files?${query.toString()}`, headers, item.blob, onProgress)
    if (answer.status >= 400) throw refusal(answer.status, answer.text, 'That file could not be uploaded.')
    return (JSON.parse(answer.text) as { id: string }).id
  },
  link: (record, body, key) =>
    asked<void>(`/records/${record.type}/${record.id}/files`, { method: 'POST', body, idempotencyKey: key }),
  caption: (record, fileId, caption, key) =>
    asked<void>(`/records/${record.type}/${record.id}/files/${fileId}`, {
      method: 'PATCH', body: { caption }, idempotencyKey: key,
    }),
  async activity(record, payload, key) {
    const made = await asked<{ id: string }>(`/records/${record.type}/${record.id}/activities`, {
      method: 'POST', body: payload, idempotencyKey: key, headers: activityHeaders(),
    })
    return made.id
  },
  online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
}

// ---------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------

/** 5 s, then doubling, never more than 5 min (§11.4). */
export function backoff(attempts: number): number {
  return Math.min(5 * 60_000, 5_000 * 2 ** Math.max(0, attempts - 1))
}

/** A refusal the server will give again — a file too big, a type it does not take. */
const final = (cause: unknown): boolean =>
  cause instanceof ApiError && (cause.status ?? 500) >= 400 && (cause.status ?? 500) < 500
  && cause.status !== 408 && cause.status !== 409 && cause.status !== 429

type Listener = (items: QueueItem[]) => void

export interface UploadQueue {
  addFile(input: Omit<FileItem, 'kind' | 'createdAt' | 'state' | 'progress' | 'linked' | 'attempts' | 'nextAt'
    | 'clientId'> & { clientId?: string }): Promise<FileItem>
  addActivity(input: Omit<ActivityItem, 'kind' | 'createdAt' | 'state' | 'progress' | 'attempts' | 'nextAt'
    | 'clientId'> & { clientId?: string }): Promise<ActivityItem>
  /** A caption typed after the photo — kept, and sent with the attach (or after it). */
  setCaption(clientId: string, caption: string): Promise<void>
  /**
   * The records a file not yet attached goes to — *Also show on the
   * property's photos* switched after the photo was taken. A record it is
   * already attached to stays attached.
   */
  setLinks(clientId: string, links: RecordRef[]): Promise<void>
  /** Takes back something that has not gone up yet; once it is up it is the record's. */
  withdraw(clientId: string): Promise<boolean>
  items(): QueueItem[]
  subscribe(listener: Listener): () => void
  /** Sends what is due, in order. One run at a time; calling it again while it runs is a no-op. */
  drain(): Promise<void>
  /** Reads what a closed app left behind. */
  load(): Promise<void>
}

export function createUploadQueue({
  store, transport, now = () => Date.now(), schedule = (run, ms) => { setTimeout(run, ms) },
}: {
  store: QueueStore
  transport: Transport
  now?: () => number
  schedule?: (run: () => void, ms: number) => void
}): UploadQueue {
  const items = new Map<string, QueueItem>()
  const listeners = new Set<Listener>()
  let running: Promise<void> | null = null
  let again = false
  let timerAt = Infinity

  const list = () => [...items.values()].sort((a, b) => a.createdAt - b.createdAt || (a.clientId < b.clientId ? -1 : 1))
  const tell = () => { const snapshot = list(); for (const listener of listeners) listener(snapshot) }

  /* Kept until the server confirms it; once it has, the stored copy goes and the tile keeps its tick. */
  const save = async (item: QueueItem) => {
    items.set(item.clientId, item)
    tell()
    if (item.state === 'done' && !(item.kind === 'file' && item.captionPending)) await store.remove(item.clientId).catch(() => {})
    else await store.put(item).catch(() => {})
  }

  const later = (item: QueueItem, cause: unknown): QueueItem => {
    const attempts = item.attempts + 1
    if (final(cause)) {
      return { ...item, attempts, state: 'failed', progress: 0, key: undefined,
        error: cause instanceof Error ? cause.message : 'That did not go up.' }
    }
    return {
      ...item, attempts, state: 'waiting', progress: 0, nextAt: now() + backoff(attempts),
      /* No answer: the same key next time, so the server replays. An answer: a new one. */
      key: cause instanceof NoAnswer ? item.key : undefined,
    }
  }

  /*
   * One step at a time, each saved before the next: ask where the bytes go,
   * send them, say they are there, then attach to each record. Every step
   * that is a request has its own key, and a step that got an answer is
   * never asked again — so a retry starts where the last attempt stopped.
   */
  const sendFile = async (start: FileItem): Promise<FileItem> => {
    let item: FileItem = { ...start, state: 'uploading', key: start.key ?? uuidv7() }
    await save(item)
    const step = async (next: FileItem) => { item = { ...next, key: uuidv7() }; await save(item) }
    const progress = (fraction: number) => {
      item = { ...item, progress: fraction }
      items.set(item.clientId, item)
      tell()
    }
    const big = item.blob.size > PRESIGN_FROM_BYTES || item.uploadKind === 'video'
    if (!item.fileId && big && !item.presigned && !item.direct) {
      const started = await transport.start({
        name: item.name, contentType: item.contentType, byteSize: item.blob.size, kind: item.uploadKind,
        ...(item.orgUnitId ? { orgUnitId: item.orgUnitId } : {}),
        ...(item.durationSeconds !== undefined ? { durationSeconds: item.durationSeconds } : {}),
      }, item.key!)
      await step(started.direct ? { ...item, direct: true }
        : { ...item, presigned: { fileId: started.fileId, url: started.url, headers: started.headers, put: false } })
    }
    if (!item.fileId && item.presigned) {
      if (!item.presigned.put) {
        try {
          await transport.put(item.presigned.url, item.presigned.headers, item.blob, progress)
        } catch (cause) {
          /* A signed URL does not last: the next try asks for a new one. */
          item = { ...item, presigned: undefined }
          await save(item)
          throw cause
        }
        await step({ ...item, presigned: { ...item.presigned, put: true } })
      }
      await transport.complete(item.presigned!.fileId, item.key!)
      await step({ ...item, fileId: item.presigned!.fileId, presigned: undefined, progress: 1 })
    }
    if (!item.fileId) {
      const fileId = await transport.direct(item, item.key!, progress)
      await step({ ...item, fileId, progress: 1 })
    }
    while (item.linked < item.links.length) {
      const record = item.links[item.linked]!
      await transport.link(record, {
        fileId: item.fileId, type: item.type, visibility: 'internal',
        ...(item.caption ? { caption: item.caption } : {}),
        ...(item.source ? { source: item.source } : {}),
      }, item.key!)
      await step({ ...item, linked: item.linked + 1 })
    }
    return { ...item, state: 'done', progress: 1, key: undefined, error: undefined }
  }

  const sendActivity = async (start: ActivityItem): Promise<ActivityItem> => {
    const item: ActivityItem = { ...start, state: 'uploading', key: start.key ?? uuidv7() }
    await save(item)
    const fileIds = item.fileClientIds
      .map((id) => items.get(id))
      .filter((file): file is FileItem => file?.kind === 'file' && file.state === 'done' && Boolean(file.fileId))
      .map((file) => file.fileId!)
    const id = await transport.activity(item.record, {
      ...item.payload, clientId: item.clientId, ...(fileIds.length > 0 ? { fileIds } : {}),
    }, item.key!)
    return { ...item, activityId: id, state: 'done', progress: 1, key: undefined, error: undefined }
  }

  /** Its files are up, or gone for good — a voice note never waits for ever on a recording. */
  const ready = (item: QueueItem): boolean => {
    if (item.kind !== 'activity') return true
    return item.fileClientIds.every((id) => {
      const file = items.get(id)
      return !file || file.state === 'done' || file.state === 'failed'
    })
  }

  const pass = async () => {
    for (;;) {
      if (!transport.online()) return
      const due = list().find((item) => (item.state === 'waiting' || item.state === 'uploading'
        || (item.kind === 'file' && item.state === 'done' && item.captionPending))
        && item.nextAt <= now() && ready(item))
      if (!due) break
      if (due.kind === 'file' && due.state === 'done') {
        /* Only its caption is left to send; the tile keeps its tick. */
        try {
          await transport.caption(due.links[0]!, due.fileId!, due.caption ?? '', due.key ?? uuidv7())
          await save({ ...due, captionPending: false, key: undefined, attempts: 0 })
        } catch (cause) {
          const attempts = due.attempts + 1
          await save(final(cause)
            ? { ...due, captionPending: false, key: undefined }
            : { ...due, attempts, nextAt: now() + backoff(attempts), key: cause instanceof NoAnswer ? due.key : undefined })
        }
        continue
      }
      try {
        const done = due.kind === 'file' ? await sendFile(due) : await sendActivity(due)
        await save(done)
      } catch (cause) {
        await save(later(items.get(due.clientId) ?? due, cause))
      }
    }
    /* The next retry, when one is waiting. */
    const next = Math.min(...list()
      .filter((item) => item.state === 'waiting' || (item.kind === 'file' && item.captionPending))
      .map((item) => item.nextAt))
    if (Number.isFinite(next) && next < timerAt) {
      timerAt = next
      schedule(() => { timerAt = Infinity; void queue.drain() }, Math.max(0, next - now()))
    }
  }

  const queue: UploadQueue = {
    async addFile(input) {
      const item: FileItem = {
        ...input, clientId: input.clientId ?? uuidv7(), kind: 'file', createdAt: now(), state: 'waiting',
        progress: 0, linked: 0, attempts: 0, nextAt: 0,
      }
      await save(item)
      void queue.drain()
      return item
    },
    async addActivity(input) {
      const item: ActivityItem = {
        ...input, clientId: input.clientId ?? uuidv7(), kind: 'activity', createdAt: now(), state: 'waiting',
        progress: 0, attempts: 0, nextAt: 0,
      }
      await save(item)
      void queue.drain()
      return item
    },
    async setCaption(clientId, caption) {
      const item = items.get(clientId)
      if (!item || item.kind !== 'file') return
      const text = caption.trim()
      if (text === (item.caption ?? '')) return
      /*
       * Not attached yet: it goes with the attach. Attached: it is an edit of
       * the file on its record, queued like everything else, so a caption
       * typed with no signal still arrives.
       */
      const attached = item.state === 'done' || item.linked > 0
      await save({ ...item, caption: text, ...(attached ? { captionPending: true, nextAt: 0, attempts: 0 } : {}) })
      if (attached) void queue.drain()
    },
    async setLinks(clientId, links) {
      const item = items.get(clientId)
      if (!item || item.kind !== 'file' || item.state === 'done') return
      const kept = item.links.slice(0, item.linked)
      const more = links.filter((link) => !kept.some((one) => one.type === link.type && one.id === link.id))
      await save({ ...item, links: [...kept, ...more] })
    },
    async withdraw(clientId) {
      const item = items.get(clientId)
      if (!item || item.state === 'done' || item.state === 'uploading') return false
      if (item.kind === 'file' && (item.fileId || item.presigned)) return false
      items.delete(clientId)
      await store.remove(clientId).catch(() => {})
      tell()
      return true
    },
    items: list,
    subscribe(listener) {
      listeners.add(listener)
      listener(list())
      return () => { listeners.delete(listener) }
    },
    drain() {
      if (running) { again = true; return running }
      /* `running` is cleared in the same step as the last look at `again`, so a call in between is never lost. */
      running = (async () => {
        try {
          do { again = false; await pass() } while (again)
        } finally {
          running = null
        }
      })()
      return running
    },
    async load() {
      const stored = await store.all().catch(() => [] as QueueItem[])
      for (const item of stored) {
        if (items.has(item.clientId)) continue
        /* An upload cut off by the app closing starts again. */
        items.set(item.clientId, item.state === 'uploading' ? { ...item, state: 'waiting', progress: 0 } : item)
      }
      tell()
      await queue.drain()
    },
  }
  return queue
}

// ---------------------------------------------------------------------------
// The one queue on this phone
// ---------------------------------------------------------------------------

let shared: UploadQueue | null = null

/** The queue, made the first time a scope screen (or the stop page) asks; it picks up what a closed app left. */
export function uploadQueue(): UploadQueue {
  if (shared) return shared
  const store = typeof indexedDB === 'undefined' ? memoryStore() : indexedDbStore()
  shared = createUploadQueue({ store, transport: httpTransport })
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { void shared?.drain() })
    void shared.load()
  }
  return shared
}
